<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Collecting this information helps your financial professional to
          fulfill regulatory obligations.
        </p>
      </div>
    </help-drawer>
    <v-form name="WealthSource" class="form" data-test-id="wealth-source-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.WEALTH_SOURCE.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="wealth-source-header">
              <span class="headerlight">
                What's your <strong>source of wealth?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your source of wealth...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isWealthSourceComplete && formHidden && !isSyncing"
          :submitted="isWealthSourceComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <v-column class="justify-center full col-12 col-sm-4" v-if="!formHidden && !isSyncing">
          <div class="faux-label required" data-test-id="wealth-source-label">Select a Source of Wealth</div>
            <div>
                <v-select
                  v-model="wealthSource"
                  :items="wealthSourceOptions"
                  :backgroundColor="inputBackgroundColor"
                  ref="wealthSourceList"
                  outlined
                  aria-required="true"
                  placeholder="Choose Source of Wealth"
                  dense
                  hide-details="auto"
                  @change="$v.wealthSource.$touch()"
                  :error-messages="wealthSourceErrors"
                  data-test-id="wealth-source-select"
                >
                </v-select>
            </div>
            <div class="skip-for-now">
              <a @click="skip()" data-test-id="skip-button">Skip for now</a>
            </div>
        </v-column>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="wealth-source-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required } from 'vuelidate/lib/validators';
import { MENUS, WEALTH_SOURCE_LIST } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'WealthSource',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    wealthSource: null,
    MENUS: MENUS,
  }),

  validations() {
    return {
      wealthSource: {
        required,
      },
      validationGroup: ['wealthSource'],
    };
  },

  computed: {
    // Is source of wealth complete
    isWealthSourceComplete() {
      return this.isPageComplete(MENUS.CLIENT.WEALTH_SOURCE.id);
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.WEALTH_SOURCE.id);
      return page && page.status === 'IN_PROGRESS';
    },
    /* Dynamic errors */
    wealthSourceErrors() {
      const errors = [];
      if (!this.$v.wealthSource.$dirty) return errors;
      !this.$v.wealthSource.required &&
        errors.push('A selection is required');
      return errors;
    },
    helpTitle() {
      return 'Why do you need this?';
    },
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {
      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;

        const data = {
          wealthSource: this.wealthSource,
        };

        // Mark page as syncing immediately for better user feedback
        this.markPageAsSyncing(this.pages[this.pageIndex]);

        this.completeStep(this.pages[this.pageIndex], data)
          .then(() => {
            // After progress refresh, recalc pages so newly added pages (e.g., Pershing Employer Risk) are included
            this.pages = this.interviewPages.getInterviewPages();
            const currentIndex = this.pages.findIndex(p => (typeof p === 'string' ? p : p.name) === MENUS.CLIENT.WEALTH_SOURCE.id);
            const to = route || this.pages[currentIndex + 1];
            // Set formHidden to true after successful completion
            this.formHidden = true;
            this.goNext(to);
          })
          .catch(() => {});
      } else {
        // If form is already hidden (completed), recalc the interview pages so any newly-added pages are respected
        this.pages = this.interviewPages.getInterviewPages();
        const currentIndex = this.pages.findIndex(p => (typeof p === 'string' ? p : p.name) === MENUS.CLIENT.WEALTH_SOURCE.id);
        const to = route || this.pages[currentIndex + 1];
        this.goNext(to);
      }
    },

    /* Nav to Previous Page in flow */
    prev() {      
      // If no employment pages, go to alternate phone
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isWealthSourceComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isWealthSourceComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.indexOf(MENUS.CLIENT.WEALTH_SOURCE.id);
    // Initialize form data for current interview
    this.formHidden = this.isWealthSourceComplete;

    this.wealthSourceOptions = [];
    Object.keys(WEALTH_SOURCE_LIST).map((key) => {
      const val = WEALTH_SOURCE_LIST[key];    
      this.wealthSourceOptions.push(val);
    });
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If wealth source is complete, hide the form
      if (this.isWealthSourceComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>
<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}
</style>
